import { isDevPreviewAnalyticsPath } from '@/lib/analytics/sanitizeAnalyticsPath'

/**
 * Phase 0 measurement integrity — canonical GA4 ID, host gate, gtag readiness.
 * Production property ID must never emit from non-production hosts.
 */

/** Canonical production GA4 measurement ID (never emit from non-prod hosts). */
export const PRODUCTION_GA_MEASUREMENT_ID = 'G-L16QPQC6ZC'

const PRODUCTION_ANALYTICS_HOSTS = new Set(['wagamour.com', 'www.wagamour.com'])

export function normalizeAnalyticsHostname(
  hostname: string | null | undefined
): string {
  return (hostname ?? '').trim().toLowerCase().split(':')[0] || ''
}

/** Hosts that must never receive the production measurement property. */
export function isNonProductionAnalyticsHost(
  hostname: string | null | undefined
): boolean {
  const host = normalizeAnalyticsHostname(hostname)
  if (!host) return false
  if (host === 'localhost' || host === '127.0.0.1') return true
  if (host.includes('wagamour-preview')) return true
  if (host.endsWith('.run.app')) return true
  if (!PRODUCTION_ANALYTICS_HOSTS.has(host)) return true
  return false
}

export function isProductionAnalyticsHost(
  hostname: string | null | undefined
): boolean {
  const host = normalizeAnalyticsHostname(hostname)
  return PRODUCTION_ANALYTICS_HOSTS.has(host)
}

/** Public measurement ID from build env only. */
export function getGaMeasurementId(): string | null {
  if (typeof process === 'undefined') return null
  const id = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim()
  return id || null
}

export function isGaAllowNonprodEnabled(): boolean {
  if (typeof process === 'undefined') return false
  return process.env.NEXT_PUBLIC_GA_ALLOW_NONPROD?.trim() === 'true'
}

export function isGaDiagnosticsEnabled(): boolean {
  if (typeof process === 'undefined') return false
  return process.env.NEXT_PUBLIC_GA_DIAGNOSTICS?.trim() === 'true'
}

/**
 * Whether GA4 scripts/events may run for this host + measurement ID + path.
 *
 * - Production hosts (wagamour.com / www): allowed when an ID is configured,
 *   except `/dev-preview` and `/dev-preview/*` (never collect to production).
 * - Non-production hosts: production ID G-L16QPQC6ZC is never allowed, even
 *   when NEXT_PUBLIC_GA_ALLOW_NONPROD=true. ALLOW_NONPROD only enables a
 *   distinct non-production measurement ID. Path deny is not applied there
 *   (localhost debug property behavior unchanged).
 * - SSR / unknown hostname: denied (fail closed; client re-checks).
 */
export function isGaCollectionAllowed(input?: {
  hostname?: string | null
  measurementId?: string | null
  pathname?: string | null
}): boolean {
  const measurementId = (
    input?.measurementId !== undefined
      ? input.measurementId
      : getGaMeasurementId()
  )
    ?.trim()
  if (!measurementId) return false

  const hostname =
    input?.hostname !== undefined
      ? input.hostname
      : typeof window !== 'undefined'
        ? window.location.hostname
        : null

  if (hostname == null || normalizeAnalyticsHostname(hostname) === '') {
    return false
  }

  const pathname =
    input?.pathname !== undefined
      ? input.pathname
      : typeof window !== 'undefined'
        ? window.location.pathname
        : ''

  if (
    isProductionAnalyticsHost(hostname) &&
    isDevPreviewAnalyticsPath(pathname)
  ) {
    return false
  }

  if (isProductionAnalyticsHost(hostname)) {
    return true
  }

  // Non-production host: hard-block production property ID.
  if (measurementId === PRODUCTION_GA_MEASUREMENT_ID) {
    return false
  }

  // Distinct non-prod ID only with explicit override.
  return isGaAllowNonprodEnabled()
}

export function isGtagReady(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function'
}

/**
 * Bounded poll until window.gtag is a function, or timeout / abort.
 * Resolves true only when gtag is ready (queued calls possible) — not network delivery.
 */
export function whenGtagReady(options?: {
  timeoutMs?: number
  intervalMs?: number
  signal?: AbortSignal
}): Promise<boolean> {
  const timeoutMs = options?.timeoutMs ?? 3000
  const intervalMs = options?.intervalMs ?? 50
  const signal = options?.signal

  if (isGtagReady()) return Promise.resolve(true)
  if (typeof window === 'undefined') return Promise.resolve(false)
  if (signal?.aborted) return Promise.resolve(false)

  return new Promise((resolve) => {
    const start = Date.now()
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const finish = (ok: boolean) => {
      if (timeoutId !== undefined) clearTimeout(timeoutId)
      signal?.removeEventListener('abort', onAbort)
      resolve(ok)
    }

    const onAbort = () => finish(false)

    signal?.addEventListener('abort', onAbort, { once: true })

    const tick = () => {
      if (signal?.aborted) {
        finish(false)
        return
      }
      if (isGtagReady()) {
        finish(true)
        return
      }
      if (Date.now() - start >= timeoutMs) {
        finish(false)
        return
      }
      timeoutId = setTimeout(tick, intervalMs)
    }

    tick()
  })
}

/** Temporary structured console diagnostics (no GA4 events). */
export function logGaDiagnostic(
  name: string,
  payload?: Record<string, unknown>
): void {
  if (!isGaDiagnosticsEnabled()) return
  if (typeof console === 'undefined' || typeof console.info !== 'function') return
  console.info('[WagamourGA]', name, {
    ...(payload || {}),
    t: new Date().toISOString(),
  })
}
